import { RegistrationFieldProps } from '../../RegistrationField.types';
import React, {
  ElementRef,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getCountryCodeByPhoneCode,
  getPhoneCodeSelectableOptions,
} from '@betty/details-attestation';
import { Box, Input, Text } from '@betty/moonlight';
import { BottomSheet, SelectableOptionItem } from '@betty/navigation';
import { Platform, Pressable, TextInput } from 'react-native';
import { AsYouType, CountryCode } from 'libphonenumber-js';

function PhoneField(
  {
    onValue,
    value,
    headerNS,
    headerKey,
    placeholderNS,
    placeholderKey,
    onSubmitEditing,
  }: RegistrationFieldProps<'phone'>,
  _: ForwardedRef<TextInput>,
) {
  const { phoneCountry, phoneNumber } = value;
  const phoneNumberRef = useRef<ElementRef<typeof Input>>(null);
  const [selectedImageSource, setSelectedImageSource] = useState<string>();
  const phoneCodeList = useMemo(getPhoneCodeSelectableOptions, []);
  const imageSource = useMemo(
    () =>
      `https://flagcdn.com/w20/${getCountryCodeByPhoneCode(value?.phoneCode || '+1')}.png`,
    [value?.phoneCode],
  );
  const formattedNumber = useMemo(
    () => new AsYouType(phoneCountry as CountryCode).input(phoneNumber),
    [phoneCountry, phoneNumber],
  );

  const onInputValue = useCallback(
    (val: string) => {
      let formattedNumber = val;

      if (formattedNumber.includes('(') && !formattedNumber.includes(')')) {
        formattedNumber = formattedNumber.replace('(', '');
        formattedNumber = formattedNumber.slice(0, formattedNumber.length - 1);
      }

      const parsedNumber = formattedNumber.replace(/\D/g, '');

      onValue(
        Object.assign({}, value, {
          phoneNumber: parsedNumber,
          phoneCode: value?.phoneCode,
          phoneCountry: value?.phoneCountry,
        }),
      );
    },
    [value, onValue],
  );

  const onPhoneCodePressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'InputSelect',
      detached: Platform.select({
        ios: false,
        web: true,
        android: false,
      }),
      data: {
        selectableOptions: phoneCodeList,
        headerNS: 'registration',
        headerKey: 'phoneCode',
        buttonNS: 'registration',
        buttonKey: 'saveButton',
        centerContent: true,
        allowEmptyField: false,
        maxHeight: '25%',
        defaultValue: value?.phoneCode,
        value: value?.phoneCode,
        onSelect: (selectedValue: SelectableOptionItem) => {
          setSelectedImageSource(selectedValue?.image?.source);
          const [phoneCode] = selectedValue.text.split(' ');
          const url = new URL(selectedValue?.image?.source || '');
          const phoneCountry =
            url.pathname.split('/').pop()?.replace('.png', '')?.toUpperCase() ||
            '';
          onValue({
            phoneCode,
            phoneNumber: value?.phoneNumber || '',
            phoneCountry,
          });
          phoneNumberRef.current?.focus();
        },
      },
    });
  }, [phoneCodeList, value?.phoneCode, value?.phoneNumber, onValue]);

  useEffect(() => {
    // on ios InteractionManager.runAfterInteractions is not working
    setTimeout(() => {
      phoneNumberRef.current?.focus();
    }, 0);
  }, []);

  return (
    <Box>
      {headerNS && headerKey && (
        <Text
          color="darkSurfacePrimary"
          marginBottom="space-300"
          variant="h5"
          locNS={headerNS}
          locKey={headerKey}
        />
      )}
      <Box flexDirection="row" gap="space-200" alignItems="center">
        <Box flex={1}>
          <Pressable onPress={onPhoneCodePressIn}>
            <Input
              image={{
                source: selectedImageSource || imageSource,
                width: 20,
              }}
              editable={false}
              placeholderNS="registration"
              placeholderKey="phoneCodeLabel"
              value={value?.phoneCode || '+1'}
              onPressIn={onPhoneCodePressIn}
            />
          </Pressable>
        </Box>
        <Box flex={2}>
          <Input
            ref={phoneNumberRef}
            value={formattedNumber}
            enterKeyHint="go"
            keyboardType="number-pad"
            placeholderNS="registration"
            placeholderKey="phoneNumberLabel"
            onChangeText={onInputValue}
            onSubmitEditing={onSubmitEditing}
          />
        </Box>
      </Box>
      {placeholderNS && placeholderKey && (
        <Text
          color="darkSurfaceSecondary"
          marginBottom="space-400"
          variant="body-s"
          locNS={placeholderNS}
          locKey={placeholderKey}
        />
      )}
    </Box>
  );
}
export default memo(forwardRef(PhoneField));
